<template>
  <WdsModal
    :show="show"
    :title="$t('Duplicate job post')"
    data-test="duplicate-job-modal"
    @cancel="handleCancel"
  >
    <div class="tw-space-y-4">
      <WdsText>
        {{
          $t(
            'This will create a new draft job post with all details pre-filled (incl. job owners and custom fields). The copy stays in draft until all details are confirmed and the job post is made available.',
          )
        }}
      </WdsText>
      <WdsInput
        v-model="duplicateTitle"
        :label="$t('New title')"
        :placeholder="$t('Enter new job post title')"
        :error="duplicateTitleError"
        :disabled="duplicating"
        data-test="duplicate-job-title-input"
        required
        @keyup.enter="handleConfirm"
      />
    </div>

    <template #footer>
      <WdsStack gap-sm justify-space-between>
        <WdsButton :disabled="duplicating" @click="handleCancel">
          {{ $t('Cancel') }}
        </WdsButton>
        <WdsButton
          primary
          :disabled="duplicating || !duplicateTitle.trim()"
          :loading="duplicating"
          @click="handleConfirm"
        >
          {{ $t('Duplicate') }}
        </WdsButton>
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script setup lang="ts">
import { WdsButton, WdsInput, WdsModal, WdsStack, WdsText } from '@worksome/wds-components'
import { ref, watch } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { DuplicateJobMutation } from '@/components/Job/graphql/mutations'

interface Props {
  show: boolean
  jobId: string
  jobTitle: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  cancel: []
  success: [newJobId: string]
}>()

const duplicateTitle = ref('')
const duplicateTitleError = ref<string | undefined>(undefined)
const duplicating = ref(false)

const { mutate: duplicateJobMutation } = useMutation(DuplicateJobMutation, {
  clientId: 'publicApi',
})

// Initialize title when modal opens
watch(
  () => props.show,
  (isShown) => {
    if (isShown) {
      duplicateTitle.value = `[Copy] ${props.jobTitle}`
      duplicateTitleError.value = undefined
    }
  },
)

const handleCancel = () => {
  duplicateTitle.value = ''
  duplicateTitleError.value = undefined
  emit('cancel')
}

const handleConfirm = async () => {
  if (!props.jobId || !duplicateTitle.value.trim()) return

  duplicating.value = true

  try {
    const result = await duplicateJobMutation({
      input: {
        id: props.jobId,
        title: duplicateTitle.value.trim(),
      },
    })

    if (!result?.data?.duplicateJob?.id) {
      throw new Error('Failed to duplicate job')
    }

    duplicating.value = false
    duplicateTitle.value = ''
    duplicateTitleError.value = undefined
    emit('success', result.data.duplicateJob.id)
  } catch (ex: any) {
    duplicating.value = false
    duplicateTitleError.value = $t('Failed to duplicate job. Please try again.')
  }
}
</script>
